import { Box, Button, Card, Flex, Grid, SimpleGrid, Skeleton, Text } from '@chakra-ui/react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import React, { ReactNode, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import MasterTable, { Column, RowData, TableType } from '../tables/base/MasterTable'
import Pagination from '../tables/base/Pagination'
import SearchInput from '../tables/base/SearchInput'
import LoadingTable from '../tables/base/LoadingTable'
import type { TableState } from '../../hooks/useTableState'

interface TableLayoutProps<T extends Record<string, unknown>> {
  tableState: TableState<T>
  columns: Column[]
  tableType: TableType
  onRowClick?: (row: T) => void
  headerActions?: ReactNode
  emptyMessage?: string
  onCopyLink?: (row: T) => void
  onOpenNewTab?: (row: T) => void
  onSearch?: (row: T) => void
  onRowIntent?: (row: T) => void
  disableMobileRowClick?: boolean
  subHeader?: ReactNode
  /** Let compact tables size to their rows instead of filling the viewport. */
  naturalContentHeight?: boolean
  /** Render the rows as cards instead of the table, e.g. for a grid view. */
  renderGrid?: (rows: T[]) => ReactNode
}

const TableLayout = React.forwardRef<
  HTMLDivElement,
  TableLayoutProps<RowData>
>(
  (
    {
      tableState,
      columns,
      tableType,
      onRowClick,
      headerActions,
      emptyMessage,
      onCopyLink,
      onOpenNewTab,
      onSearch,
      onRowIntent,
      disableMobileRowClick,
      subHeader,
      naturalContentHeight = false,
      renderGrid,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const hasLoadedOnce = useRef(false)

    useEffect(() => {
      if (tableState.totalItems > 0) {
        hasLoadedOnce.current = true
      }
    }, [tableState.totalItems])

    const allSelected =
      tableState.data.length > 0 &&
      tableState.selectedRows.length === tableState.data.length

    const isIndeterminate =
      tableState.selectedRows.length > 0 && !allSelected

    const handleSelectAll = (isChecked: boolean) => {
      if (isChecked) {
        tableState.handleSelectAll(
          true,
          tableState.data.map((row) => row.id)
        )
      } else {
        tableState.clearSelection()
      }
    }

    const contentMinHeight = naturalContentHeight
      ? 'auto'
      : { base: 'auto', md: 'calc(100vh - 300px)' }

    return (
      <Card width="100%" p="0" ref={ref}>
        <Grid minW={0}>
          <Box minW={0}>
            {subHeader}
            {/* Header: Search + Actions Row */}
            <Flex
              justifyContent="space-between"
              alignItems={{ base: 'stretch', sm: 'center' }}
              direction={{ base: 'column', sm: 'row' }}
              minHeight={{ base: 'auto', sm: '64px' }}
              flexWrap="wrap"
              gap={{ base: 3, sm: 4 }}
              px={{ base: '12px', sm: '20px' }}
              py="12px"
              minW={0}
            >
              <SearchInput onSearch={tableState.setSearch} />
              {headerActions && (
                <Flex gap="8px" flexWrap="wrap" w={{ base: '100%', sm: 'auto' }}>
                  {headerActions}
                </Flex>
              )}
            </Flex>

            {/* Table Content */}
            {tableState.isLoading ? (
              <Box minH={contentMinHeight}>
                {renderGrid ? (
                  <SimpleGrid minChildWidth="280px" gap="16px" p="12px 20px 20px">
                    {Array.from({ length: 6 }, (_, index) => <Skeleton key={index} h="236px" borderRadius="12px" />)}
                  </SimpleGrid>
                ) : (
                  <LoadingTable columns={columns} />
                )}
              </Box>
            ) : tableState.isError ? (
              /* Without this a failed request rendered as "no data", so an
                 outage looked exactly like an empty list. */
              <Box
                minH={contentMinHeight}
                py={naturalContentHeight ? '48px' : undefined}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap="10px"
              >
                <Flex w="38px" h="38px" align="center" justify="center" borderRadius="11px" bg="error.0" color="error.100">
                  <AlertTriangle size={19} />
                </Flex>
                <Box textAlign="center">
                  <Text color="solid.800" fontSize="14px" fontWeight="700">
                    {t('table.loadFailedTitle', 'Could not load this list')}
                  </Text>
                  <Text color="solid.500" fontSize="12.5px" mt="2px">
                    {t('table.loadFailedBody', 'Something went wrong on our side. Try again in a moment.')}
                  </Text>
                </Box>
                {tableState.refetch && (
                  <Button
                    size="sm"
                    variant="outline"
                    borderRadius="9px"
                    leftIcon={<RefreshCw size={14} />}
                    isLoading={tableState.isFetching}
                    onClick={() => tableState.refetch?.()}
                  >
                    {t('common.actions.tryAgain', 'Try again')}
                  </Button>
                )}
              </Box>
            ) : tableState.data.length === 0 ? (
              <Box
                minH={contentMinHeight}
                py={naturalContentHeight ? '48px' : undefined}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text color="solid.500" fontSize="14px">
                  {emptyMessage || t('table.noData')}
                </Text>
              </Box>
            ) : (
              <Box minH={contentMinHeight}>
                {renderGrid ? renderGrid(tableState.data) : (
                <MasterTable
                  columns={columns}
                  data={tableState.data}
                  onRowClick={onRowClick}
                  onRowIntent={onRowIntent}
                  onCopyLink={onCopyLink}
                  onOpenNewTab={onOpenNewTab}
                  onSearch={onSearch}
                  selectedRows={tableState.selectedRows}
                  onSelectAll={handleSelectAll}
                  onSelectRow={tableState.handleSelectRow}
                  allSelected={allSelected}
                  isIndeterminate={isIndeterminate}
                  tableType={tableType}
                  disableMobileRowClick={disableMobileRowClick}
                />
                )}
              </Box>
            )}

            {/* Pagination */}
            {(hasLoadedOnce.current || tableState.data.length > 0) && (
              <Pagination
                currentPage={tableState.page}
                totalItems={tableState.totalItems}
                itemsPerPage={tableState.itemsPerPage}
                maxPage={tableState.totalPages}
                handlePreviousPage={() => tableState.setPage(tableState.page - 1)}
                handleNextPage={() => tableState.setPage(tableState.page + 1)}
                handlePageClick={(page) => tableState.setPage(page)}
              />
            )}
          </Box>
        </Grid>
      </Card>
    )
  }
)

TableLayout.displayName = 'TableLayout'

export default TableLayout
